import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ButtonBase, Collapse, Switch, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import Select from '../../../commons/select/Select';
import { getCurrencySymbol } from '../../../conf/chargify';
import CtaButton from '../../../commons/ctaButton';
import Pill from '../Pill';
import { useStyles } from './planCard.styles';

const MAX_QUANTITY = 10;

function getQuantityData () {
    const data = [];
    for (let i = 0; i <= MAX_QUANTITY; i++) {
        data.push({ label: i, value: i });
    }
    return data;
}

// Quantity add-ons get a number selector; everything else (on/off / opt-in, and
// binary/toggle quantity handles) gets a switch — mirroring the legacy billing flow's add-on logic.
function isQuantityControl (addOn) {
    const type = addOn.type || '';
    const handle = addOn.handle || '';
    const isQuantityType = type === 'quantity' || type === 'quantity_based_component';
    const isToggleHandle = handle.indexOf('binary') !== -1 || handle.indexOf('toggle') !== -1;
    return isQuantityType && !isToggleHandle;
}

function buildDefaults (addOns) {
    const values = {};
    addOns.forEach((addOn) => {
        values[addOn.id] = isQuantityControl(addOn) ? 0 : false;
    });
    return values;
}

// aria-label always uses the natural-case verb phrase (e.g. "Choose", "Subscribe to")
// regardless of the visible CTA text, so screen readers get context even when the
// visible label is a terse override like "NEXT".
function toSentenceCase (str) {
    const lower = (str || '').toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function PlanCard ({ plan, accentColor, isCurrent, onChoosePlan, addOnsExpanded, onToggleAddOns, ctaLabel, ctaLabelPrefix }) {
    const classes = useStyles({ accentColor });
    const { price = {} } = plan;
    const symbol = getCurrencySymbol(price.currency);
    const amount = Number(price.monthly).toLocaleString();
    const name = (plan.name || '').toUpperCase();

    const [quantity] = useState(getQuantityData());
    const [addOnValues, setAddOnValues] = useState(() => buildDefaults(plan.addOns || []));

    function updateAddOnValue (id, value) {
        setAddOnValues((prev) => ({ ...prev, [id]: value }));
    }

    function renderBadges () {
        return (
            <React.Fragment>
                {isCurrent && (
                    <div className={classes.currentRibbonWrap}>
                        <Pill label="CURRENT" tone="current" />
                    </div>
                )}
                {plan.isMostPopular && (
                    <Pill
                        label="MOST POPULAR"
                        tone="popular"
                        accentColor={accentColor}
                        className={classes.pillPopularPosition}
                    />
                )}
            </React.Fragment>
        );
    }

    function renderAddOnControl (addOn) {
        if (isQuantityControl(addOn)) {
            return (
                <Select
                    marginBottom={0}
                    id={`addon-${addOn.id}`}
                    name={`addon-${addOn.id}`}
                    value={addOnValues[addOn.id]}
                    labelHeading=''
                    placeHolder='Qty'
                    dataForDropdown={quantity}
                    handleChange={(e, selectedData) => {
                        updateAddOnValue(addOn.id, selectedData.props.value);
                    }}
                />
            );
        }
        return (
            <Switch
                checked={!!addOnValues[addOn.id]}
                onChange={() => updateAddOnValue(addOn.id, !addOnValues[addOn.id])}
                color='primary'
                inputProps={{ 'aria-label': `${addOn.name} toggle` }}
            />
        );
    }

    function renderAddOns () {
        if (!plan.addOns || plan.addOns.length === 0) {
            return null;
        }
        const listId = `addons-list-${plan.id}`;
        return (
            <div className={classes.addOns}>
                <ButtonBase
                    className={classes.addOnsToggle}
                    onClick={onToggleAddOns}
                    aria-expanded={addOnsExpanded}
                    aria-controls={listId}
                >
                    Add more extra services
                    <ExpandMoreIcon
                        className={classnames(classes.addOnsToggleIcon, {
                            [classes.addOnsToggleIconExpanded]: addOnsExpanded,
                        })}
                    />
                </ButtonBase>
                <Collapse in={addOnsExpanded} id={listId} unmountOnExit>
                    {plan.addOns.map((addOn) => {
                        const perLabel = isQuantityControl(addOn) ? 'each' : 'when enabled';
                        return (
                            <div key={addOn.id} className={classes.addOnRow}>
                                <div className={classes.addOnInfo}>
                                    <span className={classes.addOnName}>{addOn.name}</span>
                                    <span className={classes.addOnPrice}>
                                        <span className={classes.addOnPriceValue}>{`${symbol}${addOn.price}`}</span>
                                        {` ${perLabel}`}
                                    </span>
                                    {addOn.description && (
                                        <Typography variant='body2' className={classes.addOnDesc}>
                                            {addOn.description}
                                        </Typography>
                                    )}
                                </div>
                                <div className={classes.addOnControl}>
                                    {renderAddOnControl(addOn)}
                                </div>
                            </div>
                        );
                    })}
                </Collapse>
            </div>
        );
    }

    return (
        <div className={classnames(classes.card, { [classes.cardPopular]: plan.isMostPopular })}>
            {renderBadges()}

            <Typography variant='h6' className={classes.planName}>
                {name}
            </Typography>

            <div className={classes.meta}>
                <Typography variant='body2' className={classes.description}>
                    {plan.description}
                </Typography>
                {plan.summary && (
                    <Typography variant='body2' className={classes.summary}>
                        {plan.summary}
                    </Typography>
                )}
            </div>

            <div className={classes.priceRow}>
                <span className={classes.priceAmount}>{symbol}{amount}</span>
                <span className={classes.pricePeriod}>/month</span>
            </div>

            <ul className={classes.features}>
                {(plan.features || []).map((feature) => (
                    <li key={feature} className={classes.feature}>
                        <CheckIcon className={classes.checkIcon} />
                        <Typography variant='body2'>{feature}</Typography>
                    </li>
                ))}
            </ul>

            {renderAddOns()}

            {!isCurrent && (
                <CtaButton
                    kind={plan.isMostPopular ? 'primary' : 'secondary'}
                    accentColor={accentColor}
                    className={classes.cta}
                    fullWidth
                    onClick={() => onChoosePlan(plan, addOnValues)}
                    aria-label={`${toSentenceCase(ctaLabelPrefix)} ${plan.name}`}
                >
                    {ctaLabel || `${ctaLabelPrefix} ${name}`}
                </CtaButton>
            )}
        </div>
    );
}

PlanCard.propTypes = {
    plan: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        summary: PropTypes.string,
        isMostPopular: PropTypes.bool,
        features: PropTypes.arrayOf(PropTypes.string),
        price: PropTypes.shape({
            monthly: PropTypes.number,
            currency: PropTypes.string,
        }),
        addOns: PropTypes.array,
    }).isRequired,
    accentColor: PropTypes.string,
    isCurrent: PropTypes.bool,
    onChoosePlan: PropTypes.func.isRequired,
    addOnsExpanded: PropTypes.bool,
    onToggleAddOns: PropTypes.func,
    ctaLabel: PropTypes.string,
    ctaLabelPrefix: PropTypes.string,
};

PlanCard.defaultProps = {
    accentColor: null,
    isCurrent: false,
    addOnsExpanded: false,
    onToggleAddOns: () => {},
    ctaLabel: '',
    ctaLabelPrefix: 'CHOOSE',
};

export default PlanCard;
